import { useEffect, useMemo, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import { map } from '../map/core/MapView';

// The toolbar filters are evaluated on the server: the rows and, when the
// map follows the list, the markers arrive already matching. What is left
// here is picking the markers to draw and the zoom that follows a filter
// change once the rows and markers for it have loaded.
export default ({
  loaded, keyword, filter, showTerminated, filterMap, markers, autoZoomOnFilter = false,
}) => {
  // Set once the filter-triggered zoom has run for the current filter state.
  // Starts set so the first load never moves the camera; the default camera
  // owns that.
  const hasZoomedRef = useRef(true);
  const firstRunRef = useRef(true);

  // Helper function to perform auto-zoom
  const performAutoZoom = (autoZoomPositions) => {
    setTimeout(() => {
      try {
        if (autoZoomPositions.length > 1) {
          const validPositions = autoZoomPositions.filter((pos) => pos && pos.latitude && pos.longitude);
          if (validPositions.length === 0) return;

          const coordinates = validPositions.map((position) => [position.longitude, position.latitude]);

          const bounds = coordinates.reduce(
            (bounds, coord) => bounds.extend(coord),
            new maplibregl.LngLatBounds(coordinates[0], coordinates[0]),
          );

          const canvas = map.getCanvas();
          if (canvas) {
            map.fitBounds(bounds, {
              duration: 1000,
              padding: Math.min(canvas.width, canvas.height) * 0.1,
            });
          }
        } else if (autoZoomPositions.length === 1) {
          const position = autoZoomPositions[0];
          if (position && position.latitude && position.longitude) {
            map.easeTo({
              center: [position.longitude, position.latitude],
              zoom: Math.max(map.getZoom(), 10),
              duration: 1000,
            });
          }
        }
      } catch (error) {
        // Silently handle errors during auto-zoom to prevent app crashes
        // The auto-zoom is not critical functionality
      }
    }, 100);
  };

  const filteredPositions = useMemo(() => Object.values(markers), [markers]);

  // Auto zoom once per filter state. The flag is cleared when a filter
  // setting changes and set again once the rows and markers for that state
  // have loaded, so live updates that arrive afterwards never move the
  // camera.
  useEffect(() => {
    if (firstRunRef.current) {
      firstRunRef.current = false;
      return;
    }
    hasZoomedRef.current = false;
  }, [keyword, filter, showTerminated, filterMap]);

  useEffect(() => {
    if (!loaded || !autoZoomOnFilter || !map || hasZoomedRef.current) return;
    hasZoomedRef.current = true;
    performAutoZoom(filteredPositions);
  }, [loaded, keyword, filter, showTerminated, filterMap, autoZoomOnFilter, filteredPositions]);

  return { filteredPositions };
};
